import { useEffect, useState } from "react";
import { Form, Col, Row, Button } from "antd";
import usePost from "../../../global/hooks/usePost";
import useGet from "../../../global/hooks/useGet";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import DeviceIcon from "../../../assets/images/vectors/SideBarIcons/DeviceIcon";
import { macValidator } from "../../../global/utils/macValidator";
import CustomTextArea from "../../../global/components/inputWidgets/CustomTextArea";
import { DuplicateMacModal } from "../../Device/Components/DuplicateMacModal";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";

/**
 * Add devices by MAC list (one MAC per line).
 *
 * Can be used two ways:
 *  1. As a modal from ProductionDeviceForm info page → props: productionId + itemId
 *  2. As a standalone page (via route)              → useParams: id + itemid
 */
export const DeviceProductionAddbyList = ({
  isModalForm,
  onCancel,
  devicesRefetch,
  LicenseNo,
  productionId: propProductionId,
  itemId: propItemId,
}) => {
  const [form] = Form.useForm();
  const { id: paramId, itemid: paramItemId } = useParams();
  const [modal, setModal] = useState(null);
  const navigate = useNavigate();

  const resolvedProductionId = propProductionId || paramId;
  const resolvedItemId = propItemId || paramItemId;

  const postUrl =
    resolvedProductionId
      ? `${process.env.REACT_APP_API_URL}/api/device-production/${resolvedProductionId}/bulk-create/`
      : null;

  const detailUrl =
    resolvedProductionId && resolvedItemId
      ? `${process.env.REACT_APP_API_URL}/api/device-production/${resolvedProductionId}/items/${resolvedItemId}/`
      : null;

  const { data } = useGet(detailUrl);
  const itemCount = data?.data?.ItemCount;
  const deviceCount = data?.data?.DeviceCount;

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const onFinish = async (values) => {
    const modValues = {
      ...values,
      production_item_id: resolvedItemId,
      LicenseNo,
    };
    try {
      await postAction(modValues);
    } catch {
      openErrorNotification("Something went wrong.");
    }
  };

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      const details = postData?.data?.details || [];
      const hasDuplicates = details.some((item) => item?.status === 400);
      if (hasDuplicates) {
        setModal(details);
        devicesRefetch?.();
      } else {
        openConfirmNotification("Success");
        devicesRefetch?.();
        isModalForm ? onCancel?.() : navigate(-1);
      }
    }
  }, [postError, postData, postLoading]);

  const breadcrumbItems = [
    {
      label: "Device Production",
      href: `/device-production/info/${resolvedProductionId}`,
    },
    { label: "Add by List", href: "#" },
  ];

  const buttonsData = [
    { label: "Apply", icon: <CheckCircleOutlined />, onClick: () => form.submit() },
    { label: "Cancel", icon: <CloseOutlined />, onClick: () => navigate(-1) },
  ];

  return (
    <>
      <Form
        validateTrigger="onChange"
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout="vertical"
        autoComplete="off"
      >
        {!isModalForm && (
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
        )}

        <div
          className={
            isModalForm ? "" : "bg-white overflow-hidden border rounded-[4px]"
          }
        >
          {!isModalForm && (
            <div className="flex items-center bg-gray-200 p-2.5">
              <DeviceIcon className="text-[18px] text-black" />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Device
              </span>
            </div>
          )}

          <div className="px-2 pt-1 pb-3">
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={isModalForm ? 24 : 12}>
                <CustomTextArea
                  label="MAC List"
                  name="mac_list"
                  placeholder={"One MAC per line\ne.g.\n00:11:22:33:44:55\nAA:BB:CC:DD:EE:FF"}
                  rows={5}
                  rules={[
                    { required: true, message: "MAC List is required" },
                    {
                      validator: async (_, value) => {
                        if (!value) return Promise.resolve();
                        const macs = value
                          .split("\n")
                          .map((v) => v.trim())
                          .filter((v) => v);
                        for (const mac of macs) {
                          try {
                            await macValidator(_, mac);
                          } catch {
                            return Promise.reject(
                              new Error(`Invalid MAC: ${mac}`),
                            );
                          }
                        }
                        if (
                          typeof itemCount === "number" &&
                          typeof deviceCount === "number" &&
                          macs.length > itemCount - deviceCount
                        ) {
                          return Promise.reject(
                            new Error(
                              `MAC Count must be ≤ ${itemCount - deviceCount}`,
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  required
                  isMacList={true}
                />
              </Col>
            </Row>
          </div>
        </div>

        {isModalForm && (
          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
              onClick={() => form.submit()}
              loading={postLoading}
              disabled={postLoading}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button className="reset-button" onClick={onCancel}>
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        )}
      </Form>

      <DuplicateMacModal
        isModalOpen={modal !== null}
        setModal={setModal}
        modal={modal}
        isModalForm={isModalForm}
      />
    </>
  );
};